import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, ChevronLeft, ChevronRight, ScrollText } from "lucide-react";

const PAGE = 50;
const ACTION_TONE = { delete: "destructive", receipt_reversed: "destructive", reject: "destructive", refund: "destructive", create: "default", receipt: "default", approve: "default" };

export default function AuditLog() {
  const [f, setF] = useState({ q: "", entity: "all", action: "all", actor: "all", date_from: "", date_to: "" });
  const [skip, setSkip] = useState(0);
  const [applied, setApplied] = useState(f);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit: PAGE, skip });
    Object.entries(applied).forEach(([k, v]) => { if (v && v !== "all") p.set(k, v); });
    api.get(`/admin/audit-log?${p}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [skip, applied]);
  useEffect(() => { load(); }, [load]);
  const apply = () => { setSkip(0); setApplied(f); };

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="Every change in the system - who did what, and when. Read-only, retained indefinitely for compliance." />
      <Card className="p-4 mb-4" data-testid="audit-filters">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search invoice no, agent, customer, detail…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && apply()} data-testid="audit-search-input" />
          </div>
          <Select value={f.entity} onValueChange={(v) => setF({ ...f, entity: v })}>
            <SelectTrigger data-testid="audit-entity-select"><SelectValue placeholder="Entity" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All entities</SelectItem>{(data?.entities || []).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.action} onValueChange={(v) => setF({ ...f, action: v })}>
            <SelectTrigger data-testid="audit-action-select"><SelectValue placeholder="Action" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All actions</SelectItem>{(data?.actions || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.actor} onValueChange={(v) => setF({ ...f, actor: v })}>
            <SelectTrigger data-testid="audit-actor-select"><SelectValue placeholder="User" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All users</SelectItem>{(data?.actors || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="audit-from-input" />
            <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="audit-to-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={() => { setF({ q: "", entity: "all", action: "all", actor: "all", date_from: "", date_to: "" }); setSkip(0); setTimeout(load, 0); }} data-testid="audit-reset-button">Reset</Button>
          <Button onClick={apply} data-testid="audit-apply-button">Apply filters</Button>
        </div>
      </Card>

      {!data ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <Card className="overflow-hidden" data-testid="audit-table">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between text-sm">
            <span className="flex items-center gap-2"><ScrollText className="w-4 h-4 text-primary" /><b data-testid="audit-total">{data.total}</b> entries{loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}</span>
            <span className="flex items-center gap-2 text-muted-foreground">
              {skip + 1}–{Math.min(skip + PAGE, data.total)}
              <Button size="icon" variant="ghost" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="audit-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)} data-testid="audit-next"><ChevronRight className="w-4 h-4" /></Button>
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium px-4 py-3">When (UTC)</th>
                  <th className="text-left font-medium px-4 py-3">Who</th>
                  <th className="text-left font-medium px-4 py-3">Action</th>
                  <th className="text-left font-medium px-4 py-3">Entity</th>
                  <th className="text-left font-medium px-4 py-3">Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className="border-b border-border/40 hover:bg-secondary/30" data-testid={`audit-row-${a.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground whitespace-nowrap">{a.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-4 py-2.5">{a.actor}</td>
                    <td className="px-4 py-2.5"><Badge variant={ACTION_TONE[a.action] || "secondary"}>{a.action}</Badge></td>
                    <td className="px-4 py-2.5 text-muted-foreground">{a.entity}</td>
                    <td className="px-4 py-2.5 max-w-xl truncate" title={a.detail}>{a.detail}</td>
                  </tr>
                ))}
                {data.items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No entries match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
